import { styled } from "@mui/material/styles";
import { Col, Row } from "react-bootstrap";
import { useState, useEffect, React } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Carousel from "../../customFiles/Carousel";
import editIcon from "../../utils/images/edit-02.svg";
import deleteIcon from "../../utils/images/delete-02.svg";
import AlertIcon from "../../utils/images/alert-02.svg";
import { useNavigate } from "react-router-dom";
import { EntityIdBasedCustomConfig, ACCESSIBILITY_LABELS } from "../../../config/constants.js";
import {
  getFormName,
  pageConfigurationCarouselIDs,
  pageConfigurationPageIDs,
} from "../../utils/constants/constants.js";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#FAFAFA",
    color: "#717680",
    fontWeight: 500,
    fontSize: "14px",
    padding: "16px",
    borderBottom: "1px solid #FFFFFF",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "14px",
    fontWeight: 400,
    color: "#47566A",
    padding: "16px",
    borderBottom: "1px solid #FFFFFF",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: "#FFFFFF",
  },
  "&:last-child td": {
    borderBottom: 0,
  },
}));


const WarningDialog = ({ open, onClose, onConfirm, billsLength }) => {
  const isLastClaim = billsLength === 1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-modal={ACCESSIBILITY_LABELS.DIALOG.MODAL}
      aria-labelledby={ACCESSIBILITY_LABELS.SAVED_BILLS.DELETE_DIALOG_TITLE}
      aria-describedby={ACCESSIBILITY_LABELS.SAVED_BILLS.DELETE_DIALOG_DESC}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "8px",
          maxWidth: "400px",
        },
      }}
    >
      <Box sx={{ padding: "24px" }}>
        <Box
          sx={{
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            backgroundColor: "#FFFAF6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
          }}
          aria-hidden="true"
        >
          <img
            src={AlertIcon}
            alt="Alert"
            aria-hidden="true"
            style={{ width: "50px", height: "50px" }}
          />
        </Box>
        <Typography
          id={ACCESSIBILITY_LABELS.SAVED_BILLS.DELETE_DIALOG_TITLE}
          sx={{
            fontSize: "18px",
            fontWeight: 600,
            color: "#252D38",
            textAlign: "center",
            marginBottom: "8px",
          }}
        >
          Are you sure you want to delete this claim?
        </Typography>

        <Typography
          id={ACCESSIBILITY_LABELS.SAVED_BILLS.DELETE_DIALOG_DESC}
          sx={{
            fontSize: "14px",
            fontWeight: 400,
            color: "#47566A",
            textAlign: "center",
            marginBottom: "24px",
          }}
        >
          {isLastClaim
            ? "Deleting this claim will permanently remove all related details, and you'll be redirected to the summary page."
            : "Deleting this claim will permanently remove all related details."}
        </Typography>

        <Box sx={{ display: "flex", gap: "12px" }}>
          <Button
            variant="outlined"
            onClick={onClose}
            aria-label={ACCESSIBILITY_LABELS.SAVED_BILLS.CANCEL_DELETE}
            sx={{
              flex: 1,
              color: "#47566A",
              borderColor: "#A4B0C0",
              borderRadius: "8px",
              padding: "10px 16px",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              "&:hover": {
                borderColor: "#8A99AD",
                backgroundColor: "#F9FAFB",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={onConfirm}
            aria-label={ACCESSIBILITY_LABELS.SAVED_BILLS.CONFIRM_DELETE}
            sx={{
              flex: 1,
              backgroundColor: "#D80008",
              color: "#FFFFFF",
              borderRadius: "8px",
              padding: "10px 16px",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#DC2626",
              },
            }}
          >
            Yes, Delete
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

const NoBillsInfoDialog = ({ open }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        navigate("/reimbursements");
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [open, navigate]);

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      aria-modal={ACCESSIBILITY_LABELS.DIALOG.MODAL}
      aria-labelledby={ACCESSIBILITY_LABELS.SAVED_BILLS.NO_BILLS_TITLE}
      aria-describedby={ACCESSIBILITY_LABELS.SAVED_BILLS.NO_BILLS_DESC}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "8px",
          maxWidth: "400px",
        },
      }}
    >
      <Box sx={{ padding: "32px 24px" }}>
        <Box
          sx={{
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            backgroundColor: "#FFFAF6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
          }}
          aria-hidden="true"
        >
          <img
            src={AlertIcon}
            alt="Alert"
            aria-hidden="true"
            style={{ width: "50px", height: "50px" }}
          />
        </Box>
        <Typography
          id={ACCESSIBILITY_LABELS.SAVED_BILLS.NO_BILLS_TITLE}
          sx={{
            fontSize: "18px",
            fontWeight: 600,
            color: "#252D38",
            textAlign: "center",
            marginBottom: "8px",
          }}
        >
          No Saved Bills
        </Typography>

        <Typography
          id={ACCESSIBILITY_LABELS.SAVED_BILLS.NO_BILLS_DESC}
          role={ACCESSIBILITY_LABELS.ROLES.ALERT}
          aria-live={ACCESSIBILITY_LABELS.ARIA_LIVE.ASSERTIVE}
          sx={{
            fontSize: "14px",
            fontWeight: 400,
            color: "#47566A",
            textAlign: "center",
          }}
        >
          There are no saved bills. Redirecting you to the summary page...
        </Typography>
      </Box>
    </Dialog>
  );
};

export default function SavedBills(props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const label =
    "I confirm that all the information provided is true and correct. In case details/documents are found to be incorrect or missing, I understand that the reimbursement may get delayed or even cancelled.";

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedBillIndex, setSelectedBillIndex] = useState(null);
  const handleDelete = () => {
    const isLastClaim = props.bills.length === 1;

    if (selectedBillIndex !== null) {
      props.deleteBillRow(selectedBillIndex);
    }

    setShowDeleteDialog(false);

    if (isLastClaim) {
      navigate("/reimbursements");
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Mobile Card View
  const MobileClaimCard = ({ row, index }) => (
    <Card
      role={ACCESSIBILITY_LABELS.ROLES.ARTICLE}
      aria-label={ACCESSIBILITY_LABELS.SAVED_BILLS.BILL_CARD(index + 1, getFormName(row.type), row.billNumber, row.billDate, row.billAmount)}
      sx={{
        marginBottom: "16px",
        borderRadius: "12px",
        boxShadow: "0 4px 10px 0 rgba(0, 0, 0, 0.05)",
      }}
    >
      <CardContent sx={{ padding: "20px" }} aria-hidden="true">
        {/* Bill Number and Type */}
        <Typography
          sx={{
            fontSize: "13px",
            color: "#2E3742",
            fontWeight: 400,
            marginBottom: "8px",
          }}
          aria-label={`Bill Number: ${row.billNumber.split('').join(' ')}`}
        >
          <span aria-hidden="true">Bill No: #{row.billNumber}</span>
        </Typography>
        <Typography
          sx={{
            fontSize: "16px",
            color: "#252D38",
            fontWeight: 500,
            marginBottom: "16px",
            lineHeight: "1.5",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {getFormName(row.type)}
        </Typography>

        <Divider sx={{ marginBottom: "16px" }} />

        {/* Bill Details */}
        <Box sx={{ marginBottom: "20px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "12px",
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                color: "#252D38",
                fontWeight: 400,
              }}
            >
              Bill Date:
            </Typography>
            <Typography
              sx={{
                fontSize: "13px",
                color: "#252D38",
                fontWeight: 400,
              }}
            >
              {row.billDate}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                color: "#252D38",
                fontWeight: 400,
              }}
            >
              Bill Amount:
            </Typography>
            <Typography
              sx={{
                fontSize: "13px",
                color: "#252D38",
                fontWeight: 400,
              }}
            >
              ₹{row.billAmount}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: "12px",
            marginTop: "20px",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<img src={editIcon} alt="editIcon" aria-hidden="true" />}
            onClick={() => props.editBillRowFunction(index)}
            aria-label={ACCESSIBILITY_LABELS.SAVED_BILLS.EDIT_BILL(index + 1)}
            sx={{
              flex: 1,
              color: "#004FB6",
              borderColor: "#72B3FF",
              borderRadius: "8px",
              padding: "10px 16px",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            startIcon={<img src={deleteIcon} alt="deleteIcon" aria-hidden="true" />}
            onClick={() => {
              setSelectedBillIndex(index);
              setShowDeleteDialog(true);
            }}
            aria-label={ACCESSIBILITY_LABELS.SAVED_BILLS.DELETE_BILL(index + 1)}
            sx={{
              flex: 1,
              color: "#AE0006",
              borderColor: "#FA9195",
              borderRadius: "8px",
              padding: "10px 16px",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            Delete
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <>
      <Row style={{ marginTop: "20px" }}>
        <Col md={12} lg={8}>
          {props.bills.length >= 0 ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <Typography
                  variant="h5"
                  aria-hidden="true"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "18px", md: "22px" },
                    color: "#252D38",
                  }}
                >
                  Claim Details
                </Typography>
                {(Array.isArray(props?.bills) && props.bills.length === 0) ||
                (EntityIdBasedCustomConfig.multipleBillUploadEntityId.includes(
                  props?.userDetails?.pmEntityId
                ) &&
                  props.bills.length <= 10) ? (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon aria-hidden="true" color="#0066DC"/>}
                    onClick={() => props?.addAnotherBill?.(true)}
                    aria-label="Add another claim"
                    sx={{
                      color: "#0066DC",
                      border: isMobile ? "none" : "1px solid #72B3FF",
                      borderRadius: "8px",
                      padding: { xs: "6px 12px", sm: "8px 16px" },
                      textTransform: "none",
                      fontSize: { xs: "14px", sm: "14px" },
                      fontWeight: 600,
                      backgroundColor: "#ffffff",
                    }}
                  >
                    Add Claim
                  </Button>
                ) : null}
              </Box>

              {isMobile ? (
                <Box>
                  {props.bills.map((row, index) => (
                    <MobileClaimCard key={index} row={row} index={index} />
                  ))}
                </Box>
              ) : (
                <Paper
                  sx={{
                    width: "100%",
                    overflow: "hidden",
                    borderRadius: "12px",
                    marginBottom: "24px",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                    border: "1px solid #E5E7EB",
                  }}
                >
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table stickyHeader aria-label={`Claim Details. ${props.bills.length} bill${props.bills.length !== 1 ? "s" : ""} added: ${props.bills.map(b => `${getFormName(b.type)}, Bill Number ${b.billNumber.split('').join(' ')}, Amount ₹${b.billAmount}`).join("; ")}`}>
                      <TableHead>
                        <TableRow>
                          <StyledTableCell sx={{ width: "30%" }}>
                            Bill Type
                          </StyledTableCell>
                          <StyledTableCell sx={{ width: "15%" }}>
                            Bill No.
                          </StyledTableCell>
                          <StyledTableCell sx={{ width: "15%" }}>
                            Bill Date
                          </StyledTableCell>
                          <StyledTableCell sx={{ width: "15%" }}>
                            Bill Amount
                          </StyledTableCell>
                          <StyledTableCell
                            align="center"
                            colSpan={2}
                            sx={{ width: "25%" }}
                          />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {props.bills.map((row, index) => (
                          <StyledTableRow key={index}>
                            <StyledTableCell
                              component="th"
                              scope="row"
                              sx={{
                                color: "#252D38 !important",
                              }}
                            >
                              {getFormName(row.type)}
                            </StyledTableCell>
                            <StyledTableCell aria-label={`Bill Number: ${row.billNumber.split('').join(' ')}`}>
                              <span aria-hidden="true">{row.billNumber}</span>
                            </StyledTableCell>
                            <StyledTableCell>{row.billDate}</StyledTableCell>
                            <StyledTableCell>₹{row.billAmount}</StyledTableCell>
                            <StyledTableCell align="center">
                              <Button
                                startIcon={<img src={editIcon} alt="editIcon" aria-hidden="true" />}
                                onClick={() => props.editBillRowFunction(index)}
                                aria-label={`Edit ${getFormName(row.type)}, Bill Number ${row.billNumber.split('').join(' ')}, Amount ₹${row.billAmount}`}
                                sx={{
                                  color: "#004FB6",
                                  textTransform: "none",
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  minWidth: "auto",
                                  padding: "4px 12px",
                                }}
                              >
                                Edit
                              </Button>
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              <Button
                                startIcon={
                                  <img src={deleteIcon} alt="deleteIcon" aria-hidden="true" />
                                }
                                onClick={() => {
                                  setSelectedBillIndex(index);
                                  setShowDeleteDialog(true);
                                }}
                                aria-label={`Delete ${getFormName(row.type)}, Bill Number ${row.billNumber.split('').join(' ')}, Amount ₹${row.billAmount}`}
                                sx={{
                                  color: "#AE0006",
                                  textTransform: "none",
                                  fontSize: "14px",
                                  fontWeight: 500,
                                  minWidth: "auto",
                                  padding: "4px 12px",
                                  "&:hover": {
                                    backgroundColor: "#FEF2F2",
                                  },
                                }}
                              >
                                Delete
                              </Button>
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
              <Box
                sx={{
                  marginTop: "32px",
                  marginBottom: "24px",
                }}
              >
                <Typography
                  component="h5"
                  aria-hidden="true"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "18px", md: "23px" },
                    color: "#252D38",
                    marginBottom: "16px",
                  }}
                >
                  Declaration
                </Typography>
                <label
                  id="declaration-label"
                  htmlFor="checkid"
                  style={{
                    wordWrap: "break-word",
                    display: "flex",
                    alignItems: "flex-start",
                    fontSize: "16px",
                    fontWeight: 400,
                    color: "#4D5C6F",
                    lineHeight: "1.6",
                    cursor: "pointer",
                  }}
                >
                  <input
                    id="checkid"
                    type="checkbox"
                    value="test"
                    required
                    aria-required="true"
                    aria-describedby="declaration-label"
                    aria-label={`Declaration: ${label}${props.isDeclarationChecked ? " Checked" : " Unchecked, required to submit"}`}
                    checked={props.isDeclarationChecked}
                    onChange={(e) =>
                      props.setIsDeclarationChecked(e.target.checked)
                    }
                    style={{
                      marginRight: "8px",
                      marginTop: "2px",
                      width: "18px",
                      height: "18px",
                      cursor: "pointer",
                      accentColor: "#0066DC",
                    }}
                  />{" "}
                  {label}
                </label>
              </Box>
            </>
          ) : (
            <></>
          )}
        </Col>
        {!isMobile ? (
          <Col md={12} lg={4} className="banners">
            <Box
              sx={{
                width: "100%",
                maxWidth: "400px",
                margin: { xs: "20px auto 0", lg: "0 auto" },
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                borderRadius: "12px",
              }}
            >
              <Carousel
                itemClassnames={"bannerImg"}
                pageId={pageConfigurationPageIDs.CREATE_CLAIM_PAGE}
                carouselId={
                  pageConfigurationCarouselIDs.REIM_CREATE_CLAIM_CAROUSEL
                }
                bannerOnClick={props.bannerClickHandler}
                slidesToShow={1}
              />
            </Box>
          </Col>
        ) : null}
      </Row>

      <WarningDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        billsLength={props.bills.length}
      />
      {!showDeleteDialog && (
        <NoBillsInfoDialog open={props.bills.length === 0} />
      )}
    </>
  );
}
